/*------------------------------------------------------------------------------------------------------------*/
/* Filename: store-payment.component.ts                                                                       */
/* Description: This file is for the component of the store payment page. It contains the logic for the page. */
/* Created: 12/Oct/2020                                                                                       */
/* Copyright (c) 2023 Lottosocial by Hatchster                                                                */
/*------------------------------------------------ -----------------------------------------------------------*/

import { AsyncPipe, NgClass, NgStyle } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  inject,
  signal
} from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatStep, MatStepLabel, MatStepper, MatStepperIcon } from "@angular/material/stepper";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { CelebrationAnimationService } from "@core/celebration-animation.service";
import { ITimer } from "@interfaces/timer";
import { IonicModule, ModalController, NavParams } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { LottieComponent } from "ngx-lottie";
import { Observable, Subject, Subscription, forkJoin, from, interval, of, throwError, timer } from "rxjs";
import { catchError, concatMap, delay, filter, map, switchMap, take, takeUntil, takeWhile, tap } from "rxjs/operators";
import { bounceIn } from "src/app/animations/custom.animation";
import { CommonVariable } from "src/app/app.constants";
import { CheckoutConfirmationComponent } from "src/app/components/checkout-confirmation/checkout-confirmation.component";
import { SyndicateLoadingComponent } from "src/app/components/syndicate-loading/syndicate-loading.component";
import { NarratorTipService } from "src/app/services/AppRootService/narrator-tip.service";
import { CashPurchaseService } from "src/app/services/cash-purchase.service";
import { CommonService } from "src/app/services/common/common.service";
import { GTMSagmentService } from "src/app/services/gtm-sagment.service";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { MixpanelService } from "src/app/services/mixpanel.service";
import { PaymentDataService } from "src/app/services/payment-data.service";
import { PaymentService } from "src/app/services/payment.service";
import Swiper from "swiper";
import type { SwiperOptions } from "swiper/types";
import { SwiperComponent, SwiperModule } from "swiper/angular";
import { CountDownTimerComponent } from "../../../components/count-down-timer/count-down-timer.component";
import { InProgressAnimationComponent } from "../../../components/in-progress-animation/in-progress-animation.component";
import { DisplayCurrencyPipe } from "../../../pipe/display-currency.pipe";
import { DayPipe } from "../../../pipe/day";
import { SafeHtmlPipe } from "../../../pipe/safehtmlpipe";
import { WalletService } from "../../account/account-wallet/wallet.services";
import { SyndicateRepositoryService } from "../../syndicate/syndicate-repository.service";
import { StoreRepositoryService } from "../store-repository.service";
import { CardOfferService } from "./card-offer.service";
import {
  PaymentCallbacks,
  PaymentResult,
  StorePaymentProcessorService
} from "./checkout-payment.service";
import { CheckoutParamsBuilderService } from "./checkout-params-builder.service";
import { CheckoutTrackingHelper } from "./checkout-tracking.helper";
import { CheckoutVoucherService } from "./checkout-voucher.service";
import { SlideToPayComponent } from "./slide-to-pay/slide-to-pay.component";
import {
  StorePaymentInitResult,
  StorePaymentInitService as StorePaymentInitOrchestratorService
} from "./store-payment-init.service";
import { VoucherService } from "src/app/services/vouchers.service";
import { ExitIntentPopup } from "src/app/components/exit-intent-popup/exit-intent-popup";
import { doc, getDoc } from "firebase/firestore";
import { Firestore } from "@angular/fire/firestore";
import { ClarityService } from "src/app/services/clarity.service";

@Component({
  selector: "app-store-payment",
  templateUrl: "./store-payment.component.html",
  styleUrls: ["./store-payment.component.scss"],
  animations: [bounceIn],
  standalone: true,
  providers: [
    CardOfferService,
    CheckoutParamsBuilderService,
    CheckoutVoucherService,
    StorePaymentProcessorService,
    CheckoutTrackingHelper,
    StorePaymentInitOrchestratorService],
  imports: [
    IonicModule,
    MatStepper,
    MatStep,
    MatStepLabel,
    FormsModule,
    InProgressAnimationComponent,
    SwiperModule,
    CountDownTimerComponent,
    NgClass,
    MatIcon,
    LottieComponent,
    MatProgressSpinner,
    MatStepperIcon,
    NgStyle,
    AsyncPipe,
    DisplayCurrencyPipe,
    DayPipe,
    SafeHtmlPipe,
    TranslatePipe,
    SlideToPayComponent],
})
export class StorePaymentComponent implements OnInit, OnDestroy {
  // slide-to-pay button
  @ViewChild("slideToPay", { static: false }) slideToPayComponent?: SlideToPayComponent;
  buttonText = "checkout_try_again.swipe_to_buy";
  expireInMin = 5; // the countdown time in minutes
  timeLeft: number = this.expireInMin * 60; // converting minutes to seconds
  expiry_in_minutes!: number;
  seconds!: number;
  private subscription!: Subscription;
  private destroy$ = new Subject<void>();
  isChecked = signal<boolean>(false);
  typeOfVoucher = signal<string>('auto');
  eventbody: Data;
  @ViewChild("stepper", { static: false }) stepper: MatStepper;
  @ViewChild("slider") slider: SwiperComponent;
  @ViewChild("storePageCardSlider") storeCardSlider: SwiperComponent;
  shopperResultUrl = "https://app.lottosocial.com/mainTabs/tabs/home/";
  offer_link_id;
  firstFormGroup: FormGroup;
  stepperIndex = 0;
  purchaseDetail = {} as any;
  cardOfferpurchaseDetail = {} as Data;
  cardOfferDetails: Data[] = []
  storedCards = [];
  isPaymentDone = false;
  showAll = false;
  promotions: Data[] = [];
  // Access service properties via getters/setters
  get activeCardIndex() { return this.paymentProcessorService.activeCardIndex; }
  get processingStates() { return this.paymentProcessorService.processingStates; }
  get addNewCardInProgress() { return this.paymentProcessorService.addNewCardInProgress; }
  set addNewCardInProgress(value: boolean) { this.paymentProcessorService.addNewCardInProgress = value; }
  get existingCardPaymentStatus() { return this.paymentProcessorService.existingCardPaymentStatus; }
  public upsell_ID: string;
  public upsellList: Data[];
  public addonAmount: Data[];
  public paymentOfferID: string;
  public updatedPrizeValue: number;
  public isCheckedValue: string;
  public checkedValues: number[];
  public checkedValuesDefaultCurrency: number[];
  public totalAmount = 0;
  tp1: string;
  tp2: string;
  tp3: string;
  t_id: string;
  other;
  prosub_id;
  selectedCard = "10";
  selectedCardForAddCardCase = "10";
  cardArrlength: number;
  journeyId: number;
  selectedCard$ = new Subject<string>();
  @ViewChild("selectCard", { static: false }) selectCard;
  upsellSummaryArray: Data[];
  private clarityService = inject(ClarityService);


  get discountVouchersStatus() { return this.checkoutVoucherService.discountVouchersStatus; }
  get discountVouchersData() { return this.checkoutVoucherService.getVouchersSnapshot(); }
  get selectedDiscountValue() { return this.checkoutVoucherService.getSelectedDiscountValue(); }
  get selectedDiscountVoucherData() { return this.checkoutVoucherService.getSelectedVoucherData(); }
  private storeString = "store";
  userId: string;
  slideToBuyBtnStatus: boolean;
  checkout_description: string;
  private offerCappingData: boolean;
  private pwaSegmentStatus: string;
  private offerId: number;
  public exit_offer_id: number;
  narratortipService = inject(NarratorTipService);
  checkoutPaymentService = inject(StorePaymentProcessorService);
  isSandbox = signal<boolean>(false);
  // This function is used to get payment details
  paymentDetails$ = this.selectedCard$.pipe(
    switchMap(() => this.walletService.paymentDetails$),
    map((paymentDetails) => {
      this.storedCards = this.walletService.getFilteredWalletCards(paymentDetails);
      if (this.storedCards?.length > 0) {
        this.showAddNewCard = true;
        this.storedCards[0].isChecked = true;
        // Also set selectedCardData so getCheckCardDetailId() can match the card
        if (!this.selectedCardData) {
          this.selectedCardData = this.storedCards[0].id;
        }
      }
      if (this.storeService.userBackout$.value) {
        this.storeService.userBackout$.next(false);
        this.goBack();
        return;
      }
      if (this.storedCards?.length == 0 && this.storeService.userBackout$.value == false) {
        this.showAddNewCard = false;
        this.addNewCard();
      }
      if (this.storedCards?.length > 2) {
        this.showAll = false;
      }
      const qp = this.route.snapshot?.queryParams ?? {};
      const isSandBoxStatus = qp.pt === "sandbox"? true : false;
      this.checkoutPaymentService.isSandbox.set(isSandBoxStatus);

      // Find the card where isChecked is true
      let checkedCard = this.storedCards.find((card) => card.isChecked === true);

      checkedCard = checkedCard?.card_type;

      if (checkedCard?.toLowerCase() === "master" || checkedCard?.toLowerCase() === "mastercard") {
        this.selectedCard = "15";
      } else {
        this.selectedCard = "10";
      }

      return this.storedCards;
    })
  );
  price;
  spinnerAnimation: boolean;
  public autoSlide: boolean;
  speed: number;
  progressBar: number;
  proceed = true;
  timer$;
  sub = new Subscription();
  private spinnerSub: Subscription;
  selectedCardData: string;
  $timer: ITimer;
  get responseObject() { return this.paymentProcessorService.responseObject; }
  set responseObject(value) { this.paymentProcessorService.responseObject = value; }
  get displayResponse() { return this.paymentProcessorService.displayResponse; }
  set displayResponse(value) { this.paymentProcessorService.displayResponse = value; }
  totalLine = 0;
  translatedToastMsg;
  selectedLanguage = "en";
  preferredCurrency: string = CommonVariable.preferredCurrency;
  preferredCurrencySignal = signal<string>(CommonVariable.preferredCurrency);
  currencySymbol: string = CommonVariable.defaultCurrencySymbol;
  currencySymbolSignal = signal<string>(CommonVariable.defaultCurrencySymbol);
  showAddNewCard: boolean;
  pType: string;
  currencyBasedPType: string;
  currencyBasedPTypeSignal = signal<string>("");
  customOfferCashbackText = "";
  customOfferAwardId = 0;
  customOfferAwardIdSubscription: Subscription;
  userData: Data;
  private checkoutComPtype = 21;
  successToast: string;
  failToast: string;
  updatedSuccessToast: string;
  updatedFailureToast: string;
  currentOfferCard: string;
  statusFlag = false;
  private capping_status = false;
  private isOneOff = false;
  constructor(
    private _formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private navParams: NavParams,
    private modalCtrl: ModalController,
    private walletService: WalletService,
    private paymentService: PaymentService,
    private changeDetRef: ChangeDetectorRef,
    private syndicateService: SyndicateRepositoryService,
    private GTMSegment: GTMSegmentService,
    public translate: TranslateService,
    private authService: AuthService,
    private celebrationAnimationService: CelebrationAnimationService,
    private storeService: StoreRepositoryService,
    private elementRef: ElementRef,
    private mixPanelService: MixpanelService,
    private checkoutTrackingHelper: CheckoutTrackingHelper,
    private storePaymentInitService: StorePaymentInitOrchestratorService,
    private matDialog: MatDialog,
    private paymentDataService: PaymentDataService,
    private cashPurchaseService: CashPurchaseService,
    public checkoutVoucherService: CheckoutVoucherService,
    private cardOfferService: CardOfferService,
    private paymentProcessorService: StorePaymentProcessorService,
    private checkoutParamsBuilder: CheckoutParamsBuilderService,
    private voucherService: VoucherService,
    private commonService: CommonService,
    private firestore: Firestore,
    @Inject(MAT_DIALOG_DATA) public data: Data
  ) {
    this.storePaymentInitService.setupConstructor({
      state: this,
      authService: this.authService,
      translate: this.translate,
      storeService: this.storeService,
      paymentService: this.paymentService,
      paymentDataService: this.paymentDataService,
      checkoutVoucherService: this.checkoutVoucherService,
      gtmSegmentService: this.GTMSegment,
      navParams: this.navParams,
      modalCtrl: this.modalCtrl,
      route: this.route,
    });
  }

  // This function is used to load the cards
  loadCards() {
    // this.isLoading = true;
    const loadCardSub = this.walletService?.paymentDetails$?.subscribe((data) => {
      this.initilizEvent(data);
      // this.isLoading = false;
      this.storedCards = data;
      if (this.storedCards?.length > 0) {
        let withPriority = this.storedCards.filter((card) => card.hasOwnProperty("priority"));
        let withoutPriority = this.storedCards.filter((card) => !card.hasOwnProperty("priority"));
        withPriority.sort((a, b) => b.priority - a.priority);
        this.storedCards = [...withPriority, ...withoutPriority];

        if (!this.storedCards.some((card) => card?.isChecked === true)) {
          this.selectedCardData = this.storedCards?.[0]?.id;
          this.storedCards[0].isChecked = true;
        } else {
          // Ensure selectedCardData stays in sync with the already-checked card
          const checkedCard = this.storedCards.find((card) => card?.isChecked === true);
          if (checkedCard && !this.selectedCardData) {
            this.selectedCardData = checkedCard.id;
          }
        }
      }
      if (this.storedCards.length > 2) {
        this.showAll = false;
      }
    });
    this.sub?.add(loadCardSub);
  }
  
  handlePaymentSuccess(encrypt_syndicate_id) {
    timer(5000)
      .pipe(
        tap(() => {
          this.slideToPayComponent?.showSuccess();
        }),
        delay(1500),
        concatMap(() =>
          from(this.authService.getConstantsMsg("1158")).pipe(
            tap((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "Congrats, you are in!",
                  line_2: "",
                },
                style: {
                  icon: "tick circle error.svg",
                  type: "Success",
                },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            }),
            catchError((error) => {
              return error;
            })
          )
        ),
        delay(2000),
        tap(() => {
          this.buttonText = "checkout_try_again.swipe_to_buy";
          this.slideToPayComponent?.reset();
          this.modalCtrl.dismiss();
          this.voucherService.setSyndicatePending().pipe(take(1)).subscribe();
          this.router.navigate(["mainTabs/tabs/syndicate"], { state: { encrypt_syndicate_id: encrypt_syndicate_id } });
          this.cashPurchaseService.toastTrigger$.next(true);
        })
      )
      .subscribe();
  }

  async onSlideToPayActivated() {
    if (this.purchaseDetail?.isTopUp) {
      this.slideToPayComponent?.showLoading();
      this.payCreditTopUp(this.purchaseDetail);
    } else {
      if (await this.handleOfferCappingGuard("store_payment_slide_to_pay")) {
        this.changeSlideBtnStatus();
        return;
      }

      const offer_id = this.purchaseDetail?.offers?.offerid;
      this.slideToPayComponent?.showLoading();

      this.cashPurchaseService.proceedToPay(offer_id).subscribe({
        next: (res) => {
          if (res?.errorCode === "OFFER_CAP_REACHED") {
            this.slideToPayComponent?.reset();
            this.changeSlideBtnStatus();
            return;
          }

          const encryptedCustomerPurchaseId = res?.encryptedCustomerPurchaseId;
          const purchaseDetail = res?.purchaseDetail;
          this.purchaseDetail.isSandbox = this.isSandbox();
          if (res && encryptedCustomerPurchaseId) {
            this.checkoutVoucherService.trackVoucherSelectionEvent(this.mixPanelService);
            let successHandled = false;
            const callbacks: PaymentCallbacks = {
              onSuccess: (result: PaymentResult) => {
                successHandled = true;
                const encrypt_syndicate_id = result?.response?.api_response?.response?.encrypt_syndicate_id;
                this.checkoutVoucherService.markSelectedVoucherAsRedeemed();
                this.handlePaymentSuccess(encrypt_syndicate_id);
              },
              onError: () => {
                if (!successHandled) {
                  this.modalCtrl.dismiss().catch((err) => err);
                    const pt = this.route.snapshot.queryParams["pt"];
                    const paymentParam = this.route.snapshot.queryParamMap.has('payment');
                    this.router.navigate(["/store/checkout-try-again"], {
                      queryParams: { offer_id: purchaseDetail?.offers?.offerid, offer_applied: paymentParam, ...(pt ? { pt } : {}) },
                      state: this.purchaseDetail,
                    });
                  this.cashPurchaseService.purchaseDetails$.next(purchaseDetail);
                }
              },
            };

            this.paymentProcessorService
              .pollPaymentStatus(encryptedCustomerPurchaseId, purchaseDetail, this.userId, callbacks)
              .subscribe({
                complete: () => {
                  if (!successHandled) {
                    this.modalCtrl.dismiss().catch((err) => err);
                    const pt = this.route.snapshot.queryParams["pt"];
                    this.router.navigate(["/store/checkout-try-again"], {
                      queryParams: { offer_id: purchaseDetail?.offers?.offerid, ...(pt ? { pt } : {}) },
                      state: this.purchaseDetail,
                    });
                    this.cashPurchaseService.purchaseDetails$.next(purchaseDetail);
                  }
                },
              });
          } else {
            this.handleSlideToPayError();
          }
        },
        error: () => {
          this.handleSlideToPayError();
        },
      });
    }
  }

  private handleSlideToPayError() {
    const defaultMsg = {
      message: {
        line_1: "Something went wrong!",
        line_2: "",
      },
      style: {
        icon: "tick circle error.svg",
        type: "Error",
      },
    };
    this.authService.showToastMessage({}, defaultMsg);
    this.slideToPayComponent?.reset();
    this.changeSlideBtnStatus();
  }

  payCreditTopUp(purchaseDetail: Data) {
    this.celebrationAnimationService.setCreditTopupStatus(true);

    this.syncCheckoutParamsBuilder();
    const context = this.checkoutParamsBuilder.buildBaseParams(this.getCheckCardDetailId());
    context.purchaseDetail = purchaseDetail;

    const callbacks: PaymentCallbacks = {
      onSuccess: (result: PaymentResult) => {
        this.isPaymentDone = true;
        this.proceed = false;
        this.stepperIndex = 1;
        this.stepper.selected.completed = true;
        this.stepper.selected.editable = false;
        this.stepper.next();
        this.clarityService.event("auto_topup_activated");
      },
      onError: (error: any) => {
        this.celebrationAnimationService.setCreditTopupStatus(false);
        this.changeSlideBtnStatus();
      },
      onFinalize: () => {
        setTimeout(() => {
          this.stopSpinner();
        }, 1500);
      },
    };

    this.subscribePayment(this.paymentProcessorService.payCreditTopUp(context, callbacks));
  }

  changeSlideBtnStatus() {
    timer(3000)
      .pipe(
        tap(() => {
          this.slideToBuyBtnStatus = false;
          return;
        })
      )
      .subscribe();
  }

  private async handleOfferCappingGuard(purchasePath: string, dismissToStore = false): Promise<boolean> {
    const isOfferCappingBlocked = await this.storeService.validateOfferCappingBeforePurchase(
      this.purchaseDetail,
      purchasePath
    );

    if (!isOfferCappingBlocked) {
      return false;
    }

    this.slideToPayComponent?.reset();
    if (this.spinnerAnimation || this.spinnerSub) {
      this.stopSpinner();
    }

    if (dismissToStore) {
      await this.modalCtrl.dismiss({ offerCappingBlocked: true }).catch(() => undefined);
      await this.router.navigateByUrl("/mainTabs/tabs/store").catch((error) => {
        this.authService.errorHandlerLogs("[OfferCapping] Failed to redirect user after checkout block", error);
        return false;
      });
    }

    return true;
  }

  async ngOnInit() {
    this.getPwasegementData();
    if (await this.handleOfferCappingGuard("store_payment_component_init", true)) {
      return;
    }

    const initResult: StorePaymentInitResult = await this.storePaymentInitService.initialize({
      authService: this.authService,
      cashPurchaseService: this.cashPurchaseService,
      gtmSegmentService: this.GTMSegment,
      cardOfferService: this.cardOfferService,
      paymentProcessorService: this.paymentProcessorService,
      formBuilder: this._formBuilder,
      route: this.route,
      destroy$: this.destroy$,
      preferredCurrency: this.preferredCurrencySignal(),
      onSetSlideToBuyStatus: (enabled: boolean) => {
        this.slideToBuyBtnStatus = enabled;
      },
      onSetPromotions: (promotions: Data[]) => {
        this.promotions = promotions || [];
      },
      onSetOfferTimer: (minutes: number, seconds: number) => {
        this.expiry_in_minutes = minutes;
        this.seconds = seconds;
      },
      onStartCardCountdowntimer: () => this.CardCountdowntimer(),
      onLoadCards: () => this.loadCards(),
      onGetUpsellData: (upsellId: string) => this.getUpsellData(upsellId),
    });

    this.userId = initResult.userId;
    this.journeyId = initResult.journeyId;
    this.firstFormGroup = initResult.firstFormGroup;
    this.prosub_id = initResult.prosub_id;
    this.tp1 = initResult.tp1;
    this.tp2 = initResult.tp2;
    this.tp3 = initResult.tp3;
    this.t_id = initResult.t_id;
    this.other = initResult.other;
    this.upsell_ID = initResult.upsell_ID;
    this.paymentOfferID = initResult.paymentOfferID;

    window.addEventListener('beforeunload', this.handleRefresh);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.customOfferAwardIdSubscription?.unsubscribe();
    this.subscription?.unsubscribe();
    this.checkoutVoucherService.destroy();
    this.sub?.unsubscribe();
    this.spinnerSub?.unsubscribe();
    clearInterval(this.interval);
    this.cardOfferService.destroy();
    window.removeEventListener('beforeunload', this.handleRefresh);
  }

  // this can be used to implement logic which is necessary when the user refreshes the page
  handleRefresh = (event: BeforeUnloadEvent): void => {
    this.checkoutVoucherService.resetHideStatusOnExit(this.paymentProcessorService.existingCardPaymentStatus);
  };

  // This method is to open tickets
  openTickets() {
    this.storeService.customOfferCashBackText$.next("");
    this.storeService.customOfferAwardId$.next(0);
    if (this.purchaseDetail.isCreatingPS) {
      this.modalCtrl.dismiss({ saveTicket: true }).catch((err) => {
        return err;
      });
      return;
    }
    if (this.purchaseDetail.isTopUp) {
      this.celebrationAnimationService.setCreditTopupStatus(false);
      this.celebrationAnimationService.trackCelebrations();
      this.modalCtrl.dismiss().catch((err) => {
        return err;
      });
      return;
    }
    setTimeout(() => {
      this.checkoutTrackingHelper.persistScrollDepth(this.elementRef, "productScroll");
    });
    localStorage.setItem("datatoSyndi", this.journeyId.toString());
    this.modalCtrl
      .dismiss({
        syndicateId: this.responseObject?.syndicate_id,
        p_type: this.responseObject?.p_type ? this.responseObject?.p_type : "10",
      })
      .catch((err) => {
        return err;
      });
  }

  // This function maps through the storedCards array and sets the isChecked property to true
  // if the card is the card that was clicked. Otherwise, it sets the isChecked property to false.
  changeCheckCard(storedCards, index) {
    if (storedCards.length === 1) {
      setTimeout(() => {
        storedCards[index].isChecked = true;
      }, 100);
      return;
    }

    storedCards = storedCards.map((card, cIndex) => {
      if (index === cIndex) {
        card.isChecked = true;
      } else {
        card.isChecked = false;
      }
      return card;
    });

    const checkedCard = storedCards.find((card) => card.isChecked);
    this.updateDropdownValue(checkedCard.card_type);
  }

  updateDropdownValue(cardType: string) {
    if (cardType?.toLowerCase() === "mastercard" || cardType?.toLowerCase() === "master") {
      this.selectCard.value = "15";
    } else {
      this.selectCard.value = "10";
    }
  }

  // This function starts the spinner animation, and sets the progress bar to 0. It then creates a timer that will increment the progress bar every 20 milliseconds. If the timer is successful, the progress bar will be incremented by 1 for every 20 milliseconds. If the timer fails, it will return an error message. This function also adds the timer subscription to the subscriptions array.
  startSpinner() {
    setTimeout(() => {
      this.checkoutTrackingHelper.trackPayNowClicked(this.data, this.journeyId);
    });
    this.spinnerAnimation = true;
    this.progressBar = 0;
    this.timer$ = interval(20);
    this.spinnerSub = this.timer$.subscribe(
      (sec) => {
        this.progressBar = 0 + sec;
      },
      (err) => {
        return err;
      }
    );
  }

  // This function is used to process payments after user click pay now button
  async proceedToPay(stepper: MatStepper) {
    if (await this.handleOfferCappingGuard("store_payment_pay_now")) {
      return;
    }

    if (this.purchaseDetail.isTopUp) {
      this.celebrationAnimationService.setCreditTopupStatus(true);
    }

    this.syncCheckoutParamsBuilder();
    const context = this.checkoutParamsBuilder.buildFullParams(
      this.checkoutVoucherService,
      this.getCheckCardDetailId()
    );

    const callbacks: PaymentCallbacks = {
      onSuccess: (result: PaymentResult) => {
        if (this.purchaseDetail.isTopUp) {
          this.isPaymentDone = true;
          this.proceed = false;
          this.stepperIndex = 1;
          stepper.selected.completed = true;
          stepper.selected.editable = false;
          stepper.next();
        } else {
          this.checkoutVoucherService.trackVoucherSelectionEvent(this.mixPanelService);
          this.checkoutVoucherService.markSelectedVoucherAsRedeemed();
          const customOfferToast = localStorage.getItem("custom-offer-toast");
          if (customOfferToast === "true" && this.customOfferAwardId && this.customOfferAwardId > 0) {
            this.showCreditIssueToast();
          }
          this.isPaymentDone = true;
          this.proceed = false;
          this.stepperIndex = 1;
          this.cardOfferService.startCountdown();
          stepper.selected.completed = true;
          stepper.selected.editable = false;
          stepper.next();
          this.showTicketWaiting().catch((err) => {
            this.authService.errorHandlerLogs('Error in showTicketWaiting', err);
          });
          this.responseObject = this.paymentProcessorService.responseObject;
          this.displayResponse = this.paymentProcessorService.displayResponse;
          if (result.encryptedCustomerPurchaseId) {
            this.trackPaymentStatus(result.encryptedCustomerPurchaseId);
          }
        }
      },
      onError: (error: any) => {
        if (this.purchaseDetail.isTopUp) {
          this.celebrationAnimationService.setCreditTopupStatus(false);
        }
      },
      onFinalize: () => {
        setTimeout(() => {
          this.stopSpinner();
        }, 1500);
      },
    };

    this.subscribePayment(this.paymentProcessorService.proceedToPay(context, callbacks));
  }


  // This function is used to process payments after user click pay now button for private syndicates
  proceedToPayForPS(stepper: MatStepper) {
    this.syncCheckoutParamsBuilder();
    const context = this.checkoutParamsBuilder.buildBaseParams(this.getCheckCardDetailId());

    const callbacks: PaymentCallbacks = {
      onSuccess: (result: PaymentResult) => {
        this.purchaseDetail.status = "completed";
        this.syndicateService.saveSyndicatPurchaseDetails(
          this.purchaseDetail?.syndicate_id,
          this.purchaseDetail
        );
        this.isPaymentDone = true;
        this.proceed = false;
        this.stepperIndex = 1;
        stepper.selected.completed = true;
        stepper.selected.editable = false;
        stepper.next();
        this.responseObject = this.paymentProcessorService.responseObject;
      },
      onFinalize: () => {
        setTimeout(() => {
          this.stopSpinner();
        }, 1500);
      },
    };

    this.subscribePayment(this.paymentProcessorService.proceedToPayForPS(context, callbacks));
  }

  //this function is called when the user changes the value of the dropdown
  onChange(selectCardValue) {
    this.selectedCard = selectCardValue;
  }

  // This function is used for add new card functionalities
  async addNewCard() {
    if (await this.handleOfferCappingGuard("store_payment_add_new_card")) {
      return;
    }

    this.syncCheckoutParamsBuilder();
    const context = this.checkoutParamsBuilder.buildAddNewCardParams(this.checkoutVoucherService, {
      dialogData: this.data,
      queryParams: this.route.snapshot.queryParams || {},
      scrollDepthProvider: () =>
        Number(this.checkoutTrackingHelper.persistScrollDepth(this.elementRef, "productScroll")),
      cardDetailId: this.getCheckCardDetailId(),
    });

    const callbacks: PaymentCallbacks = {
      onDismissModal: () => this.modalCtrl.dismiss(),
      onNavigate: (path: string, state?: Data, queryParams?: Data) =>
        this.router.navigate([path], { state, queryParams }),
    };
   console.info('Context for addNewCard', context, callbacks);
    await this.paymentProcessorService.addNewCard(context, callbacks);
  }

  showAddCardErrorToast() {
    this.authService
      .getConstantsMsg("1086")
      .then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: "something went wrong on select card",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: CommonVariable.error,
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      })
      .catch((err) => {
        return err;
      });
  }

  /**
   * This function is used to activate the trigger when the user clicks on the
   * 'Activate Offer' button.
   */
  activateTrigger() {
    this.syndicateService
      .activateTrigger(this.offer_link_id)
      .pipe(
        catchError((error) => {
          // RB #24242 fix
          return throwError(`Error in activateTrigger method ${error}`);
        })
      )
      .subscribe({
        next: (res) => {
          if (res?.status === "rejected") {
            this.authService
              .getConstantsMsg(res?.error_code)
              .then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Something went wrong!",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                this.authService.showToastMessage(translatedMsg, defaultMsg);
              })
              .catch((err) => {
                this.authService.errorHandlerLogs('Error fetching constants message', err);
              });
            return;
          }
        },
        error: () => {
          this.authService
            .getConstantsMsg("1050")
            .then((translatedMsg) => {
              const defaultMsg = {
                message: { line_1: "Something went wrong!", line_2: "" },
                style: { icon: "tick circle error.svg", type: "Error" },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            })
            .catch((error) => {
              this.authService.errorHandlerLogs('Error fetching constants message', error);
            });
        }
      });
  }

  // This function is to stop the spinner animation
  // It sets the progress bar to 100%
  // It unsubscribes from the subscription
  // It sets the spinnerAnimation variable to false
  // It sets the proceed variable to true
  // It calls changeDetRef.detectChanges() method
  stopSpinner() {
    this.progressBar = 100;
    this.spinnerSub?.unsubscribe();
    this.spinnerAnimation = false;
    this.proceed = true;
    this.changeDetRef.detectChanges();
  }

  /**
   * Snapshots component fields into CheckoutParamsBuilderService before building PaymentContext.
   */
  private syncCheckoutParamsBuilder(options?: { preferredCurrencySource?: "signal" | "property" }): void {
    const src = options?.preferredCurrencySource ?? "signal";
    this.checkoutParamsBuilder.bind({
      purchaseDetail: this.purchaseDetail,
      userId: this.userId,
      preferredCurrency: src === "signal" ? this.preferredCurrencySignal() : this.preferredCurrency,
      preferredCurrencySignal: () => this.preferredCurrencySignal(),
      selectedCard: this.selectedCard,
      selectedCardForAddCardCase: this.selectedCardForAddCardCase,
      prosub_id: this.prosub_id,
      tp1: this.tp1,
      tp2: this.tp2,
      tp3: this.tp3,
      t_id: this.t_id,
      upsell_ID: this.upsell_ID,
      paymentOfferID: this.paymentOfferID,
      other: this.other,
      journeyId: this.journeyId,
      cardArrlength: this.cardArrlength,
      updatedPrizeValue: this.updatedPrizeValue,
      customOfferAwardId: this.customOfferAwardId,
      userData: this.userData,
      checkoutComPtype: this.checkoutComPtype,
      statusFlag: this.statusFlag,
      updatedSuccessToast: this.updatedSuccessToast,
      updatedFailureToast: this.updatedFailureToast,
      addonAmount: this.addonAmount,
      totalAmount: this.totalAmount,
      isCheckedValue: this.isCheckedValue,
    });
  }

  private subscribePayment(obs: Observable<PaymentResult>): void {
    obs.subscribe({
      error: (err) => {
        this.authService.errorHandlerLogs("Store payment flow error", err);
      },
    });
  }

  // This function returns the ID of the card that is currently checked by the user.
  getCheckCardDetailId() {
    return this.paymentProcessorService.getCheckCardDetailId(this.storedCards, this.selectedCardData);
  }

  // this function navigates back to the previous page
  // it uses the modal controller to dismiss the current page
  // it returns an error if the page is not dismissed
  goBack() {
    this.checkoutVoucherService.resetHideStatusOnExit(this.paymentProcessorService.existingCardPaymentStatus);
    this.storeService.customOfferCashBackText$.next("");
    this.storeService.customOfferAwardId$.next(0);
    const currentUrl = this.router.url;
    if (currentUrl.includes("payment=")) {
      const baseUrl = currentUrl.split("?")[0];
      this.router.navigateByUrl(baseUrl);
    }
    this.celebrationAnimationService.setCreditTopupStatus(false);
    this.modalCtrl.dismiss().catch((err) => {
      return err;
    });
  }

  openConfirmationPopup() {
    const confirmationDialog = this.matDialog.open(CheckoutConfirmationComponent, {
      panelClass: "mysterybox-dialog-size",
      backdropClass: "mysterybox-back-drop-color",
      hasBackdrop: true,
      disableClose: false,
    });

    confirmationDialog.afterClosed().subscribe({
      next: (response: boolean) => {
        if (response) {
          this.goBack();
        }
      },
      error: (error) => {
        return error;
      },
    });
  }

  getPwasegementData() {
    this.commonService.getUid("PwasegementData")
      .pipe(
        take(1),
        switchMap(async (uid: string) => {
          if (!uid) {
            return null;
          }
          const userDocPath = `users/${uid}`;
          try {
            const userSnap = await getDoc(doc(this.firestore, userDocPath));
            if (!userSnap.exists()) {
              return null;
            }
            const userData = userSnap.data();
            this.pwaSegmentStatus = userData?.['pwa_segment_status'];
            return uid;
          } catch (err) {
            return null;
          }
        })
      )
      .subscribe({
        next: (uid) => {
          if (!uid) return;

          this.commonService.slide$
            .pipe(
              filter((slide): slide is Data => !!slide),
              take(1),
              takeUntil(this.destroy$)
            )
            .subscribe(async (slide) => {
              this.isOneOff = slide?.isOneOff ?? false;
              this.offerId = slide?.slide_info?.offer_id;
              this.exit_offer_id = slide?.exit_offer_id;
              const offerCappingPath = `users/${uid}/offer_capping/${this.exit_offer_id}`;

              const snap = await getDoc(doc(this.firestore, offerCappingPath));
              if (snap.exists()) {
                const data = snap.data();
                this.capping_status = data?.capping_status ?? false;
              }
            });
        },

        error: (err) => {
          return err;
        }
      });
  }

  handleExitIntentPopup() {
    // Assign directly (no ?? fallback) so a prior slide's exit_offer_id cannot stick.
    const slide = this.commonService.getSlideData();
    if (slide) {
      this.isOneOff = slide?.isOneOff ?? false;
      this.exit_offer_id = slide?.exit_offer_id;
    }

    const paymentParam = this.route.snapshot.queryParamMap.get('payment');

    // Highest priority: payment flow
    if (paymentParam || this.data?.isStoreOffer || this.isOneOff || this.data?.page === 'store') {
      this.openConfirmationPopup();
      return;
    }

    if (this.pwaSegmentStatus === 'subscriber' || this.capping_status || !this.exit_offer_id) {
      this.openConfirmationPopup();
      return;
    }

    this.openExitIntentPopup(this.exit_offer_id);
  }

  openExitIntentPopup(offerid: number) {
    const confirmationDialog = this.matDialog.open(ExitIntentPopup, {
      panelClass: "mysterybox-dialog-size",
      backdropClass: "mysterybox-back-drop-color",
      hasBackdrop: true,
      disableClose: false,
      data: { offerId: offerid }
    });

    confirmationDialog
      .afterClosed()
      .pipe()
      .subscribe({
        next: (response: boolean) => {
          if (response) {
            this.goBack();
          }
        },
        error: (error) => {
          return error;
        },
      });
  }

  async showTicketWaiting() {
    const syndicateTickets = await this.modalCtrl.create({
      component: SyndicateLoadingComponent,
      cssClass: "modal-fullScreen",
    });
    return await syndicateTickets.present();
  }

  updateCustomerPurchaseDocWithNdc(ndcId: string, customerPurchaseId: string): void {
    this.paymentService.updateCustomerPurchaseWithNdc(customerPurchaseId, ndcId).subscribe({
      next: () => {
        // Do nothing in success case
      },
      error: (err) => {
        this.authService.errorHandlerLogs('updateCustomerPurchaseWithNdc failed', err);
      }
    });
  }

  private showCreditIssueToast(): void {
    const userId = this.userId;
    const awardId = this.customOfferAwardId;
    localStorage.removeItem("custom-offer-toast");
    this.authService.customOfferCreditCheck(userId, awardId).subscribe({
      next: (res) => {
        if (res?.status == "success") {
          this.authService
            .getConstantsMsg("1114")
            .then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "Credit issued to your account",
                  line_2: "",
                },
                style: {
                  duration: 2000,
                  icon: "tick circle success.png",
                  type: "Success",
                },
              };
              this.authService?.showToastMessage(translatedMsg, defaultMsg);
            })
            .catch((error) => {
              this.authService.errorHandlerLogs('Error fetching constants message', error);
            });
        }
      },
      error: (err) => {
        this.authService.errorHandlerLogs('customOfferCreditCheck failed', err);
      }
    });
  }
  initilizEvent(data) {
    // Handle null/undefined data gracefully
    const cards = Array.isArray(data) ? data : [];

    this.cardArrlength = cards.length;
    this.checkoutTrackingHelper.trackCheckoutViewedForCards(
      this.data,
      this.journeyId,
      this.cardArrlength,
      this.data?.offers?.offerid ?? ""
    );
  }
  // get upsell data
  getUpsellData(upsell_ID: string) {
    try {
      this.paymentService.getUpsellList(upsell_ID).subscribe({
        next: (data) => {
          if (data?.upsell_list) {
            this.upsellList = data?.upsell_list;
          }
        },
        error: (err) => {
          this.authService.errorHandlerLogs('getUpsellList failed', err);
        }
      });
    } catch (error) {
      this.authService.errorHandlerLogs('getUpsellData exception', error);
      return error;
    }
  }

  // on checkbox change
  checkBoxChanged(event: Data, offerData: Data) {
    const isChecked = event?.detail?.checked;
    this.isCheckedValue = offerData?.offer_id;
    const prizeValue = event?.detail?.value;
    const prizeValueDefault = offerData?.price["GBP"];
    this.addonAmount = this.purchaseDetail?.add_on ?? [];
    if (isChecked) {
      // If the checkbox is checked, add the calculated additionOfPrize to the array of checkedValues
      this.checkedValues.push(prizeValue);
      this.checkedValuesDefaultCurrency.push(prizeValueDefault);

      this.addonAmount.push({
        offer_id: offerData.offer_id,
        value: offerData.price["GBP"],
        product_description: offerData?.product_description || "offer",
        preferred_currency: {
          Value: offerData.price[this.preferredCurrency],
          currency: this.preferredCurrency,
        },
      });
      this.upsellSummaryArray.push({
        offer_id: offerData.offer_id ?? "",
        summary_content: offerData?.summary_content ?? {},
        product_description: offerData?.product_description || "offer",
      })
    } else {
      // If the checkbox is unchecked, remove the original prizeValue from the array of checkedValues
      const index = this.checkedValues.indexOf(prizeValue);
      if (index !== -1) {
        this.checkedValues.splice(index, 1);
        this.checkedValuesDefaultCurrency.splice(index, 1);
      }
      // Remove the corresponding addonAmount entry
      const offerIdToRemove = offerData.offer_id;
      this.addonAmount = this.addonAmount.filter(addon => addon.offer_id !== offerIdToRemove);
      this.upsellSummaryArray = this.upsellSummaryArray?.filter((addon) => addon?.offer_id !== offerIdToRemove);
    }
    this.purchaseDetail.addons = this.addonAmount;
    // Calculate the total prize by summing the values of all checked checkboxes
    const totalPrizeValues = this.checkedValues.reduce((acc, currentValue) => acc + Number(currentValue), 0);
    this.totalAmount = totalPrizeValues;
    const totalPrize = Number(totalPrizeValues.toFixed(2));
    this.updatedPrizeValue = totalPrize;
  }

  storeSliderOptions1: SwiperOptions = {
    autoplay: false,
    speed: 500,
    spaceBetween: 0,
    // Keep 1.5 on desktop too: checkout column is phone-width; slidesPerView:1 stretched the ticket past the summary and clipped Apply.
    slidesPerView: 1.5,
    centerInsufficientSlides: true,
    edgeSwipeThreshold: 4,
    grabCursor: true,
  };

  storeSliderOptions2: SwiperOptions = {
    speed: 500,
    slidesPerView: 1.15,
    centeredSlides: true,
    pagination: {
      el: ".swiper-pagination",
      clickable: true,
    },
    // Disabled: slides contain CTA buttons that navigate away. Swiper's rAF callback races
    // with ionViewWillLeave(), leaving params undefined. Explicit false prevents regressions
    // if Swiper defaults or shared configs change. LOTTOSOCIAL-PWA-1NCK
    slideToClickedSlide: false,
    autoplay: {
      delay: 5000,
    },
  };

  slides: Swiper;
  interval;
  setSwiperInstance(swiper: Swiper) {
    this.slides = swiper;

    // Clear any existing intervals
    clearInterval(this.interval);

    // Start a new interval after a delay to check and control autoplay
    setTimeout(() => {
      this.interval = setInterval(() => {
        // Check if slides or autoplay is undefined
        if (!this.slides || !this.slides.autoplay) {
          return;
        }

        if (!this.autoSlide) {
          // If autoSlide is false, stop autoplay
          this.slides?.autoplay?.stop();
        } else {
          // If autoSlide is true, start or resume autoplay
          this.slides.slideNext(this.speed);
          this.slides.autoplay.start();
        }
      }, 4500);
    }, 4500);
  }

  prev() {
    try {
      this.slider?.swiperRef?.slidePrev(500);
    } catch (e) {
      return e;
    }
  }
  next() {
    try {
      this.slider?.swiperRef?.slideNext(500);
    } catch (e) {
      return e;
    }
  }

  async fetchOrderData(): Promise<void> {
    const result = await this.cardOfferService.fetchOrderData(
      this.userId,
      this.preferredCurrencySignal()
    );
    this.promotions = result.promotions || [];
    this.cardOfferDetails = result.cardOfferDetails || [];
    this.successToast = result.successToast || "";
    this.failToast = result.failToast || "";
  }
  cardOfferDisabled(index: number) {
    return this.cardOfferService.isCardDisabled(index);
  }

  cardOfferPayment(item: Data, index: number) {
    // Find the matching card offer detail
    this.cardOfferDetails.forEach((cardoffer) => {
      if (cardoffer.offerid === item.offer_id) {
        this.cardOfferpurchaseDetail = cardoffer;
        this.currentOfferCard = item.product_title;
        this.updatedSuccessToast = this.successToast?.replace("[product_title]", this.currentOfferCard) || "";
        this.updatedFailureToast = this.failToast?.replace("[product_title]", this.currentOfferCard) || "";
        this.cardofferProceedToPay(index);
      }
    });
  }

  CardCountdowntimer() {
    this.subscription = interval(1000).pipe(
      takeWhile(() => this.timeLeft > 0)
    ).subscribe(() => {
      this.timeLeft--;
      this.expiry_in_minutes = Math.floor(this.timeLeft / 60);
      this.seconds = this.timeLeft % 60;
    });
  }


  //this fucnction is used to process payments for the confirmation page card offers
  cardofferProceedToPay(index: number) {
    this.syncCheckoutParamsBuilder({ preferredCurrencySource: "property" });
    const context = this.checkoutParamsBuilder.buildCardOfferParams(this.getCheckCardDetailId());

    const callbacks: PaymentCallbacks = {
      onSuccess: (result: PaymentResult) => {
        this.paymentProcessorService.activeCardIndex.push(index);
        this.isPaymentDone = true;
        this.proceed = false;
        this.responseObject = this.paymentProcessorService.responseObject;
        this.displayResponse = this.paymentProcessorService.displayResponse;
      },
      onFinalize: () => {
        setTimeout(() => {
          this.eventbody = this.checkoutTrackingHelper.getPayNowPaymentStatusEventBody(
            this.data,
            this.journeyId,
            this.cardArrlength,
            this.selectedCard
          );
          this.stopSpinner();
        }, 1500);
      },
    };

    this.subscribePayment(
      this.paymentProcessorService.cardofferProceedToPay(
        index,
        context,
        this.cardOfferpurchaseDetail,
        callbacks
      )
    );
  }

  onVoucherSelect(event: Data, selectedVoucher: Data, triggerType?: string) {
    this.isChecked.set(event?.detail?.checked ?? selectedVoucher?.isSelected);
    this.typeOfVoucher.set("manual");
    this.checkoutVoucherService.selectVoucher(event, selectedVoucher, triggerType);
  }

  trackPaymentStatus(cpId: string) {
    this.checkoutTrackingHelper.trackPaymentStatusForPurchase(
      this.paymentService,
      cpId,
      this.data,
      this.journeyId,
      this.cardArrlength,
      this.selectedCard
    );
  }
}
